import { useEffect, useRef, useState } from "react";

const API = process.env.REACT_APP_BACKEND_URL;
const cache = new Map();

async function fetchSuggestions(q) {
  const key = q.toLowerCase();
  if (cache.has(key)) return cache.get(key);
  try {
    const r = await fetch(`${API}/api/recipients/suggest?q=${encodeURIComponent(q)}`, { credentials: "include" });
    const data = r.ok ? await r.json() : [];
    cache.set(key, data);
    setTimeout(() => cache.delete(key), 60000);
    return data;
  } catch {
    return [];
  }
}

/** Drop-in <input> replacement that suggests previously-used email addresses.
 *  Same props as <input>. `multi` = comma-separated recipients (suggests for the last token).
 *  `extra` = additional {email,name} entries to merge (e.g. saved contacts). */
export const EmailSuggestInput = ({ value = "", onChange, multi = false, extra = [], wrapClassName, theme = "dark", ...props }) => {
  const light = theme === "light";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const wrapRef = useRef(null);
  const reqRef = useRef(0);

  const parts = multi ? String(value).split(",") : [String(value)];
  const token = parts[parts.length - 1].trim();

  useEffect(() => {
    if (!token) { setItems([]); setOpen(false); return; }
    const id = ++reqRef.current;
    const t = setTimeout(async () => {
      const remote = await fetchSuggestions(token);
      if (id !== reqRef.current) return;
      const q = token.toLowerCase();
      const local = (extra || []).filter((c) => c.email && (c.email.toLowerCase().includes(q) || (c.name || "").toLowerCase().includes(q)));
      const seen = new Set();
      const merged = [...remote, ...local].filter((c) => {
        const e = (c.email || "").toLowerCase();
        if (!e || seen.has(e) || e === q) return false;
        seen.add(e);
        return true;
      }).slice(0, 8);
      setItems(merged);
      setOpen(merged.length > 0);
      setActive(-1);
    }, 120);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    const onDoc = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const pick = (email) => {
    let next;
    if (multi) {
      const head = parts.slice(0, -1).map((p) => p.trim()).filter(Boolean);
      next = [...head, email].join(", ") + ", ";
    } else {
      next = email;
    }
    onChange?.({ target: { value: next, name: props.name } });
    setOpen(false);
    setItems([]);
  };

  const onKeyDown = (e) => {
    if (!open || items.length === 0) return props.onKeyDown?.(e);
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => (a + 1) % items.length); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => (a <= 0 ? items.length - 1 : a - 1)); }
    else if ((e.key === "Enter" || e.key === "Tab") && active >= 0) { e.preventDefault(); pick(items[active].email); }
    else if (e.key === "Escape") { setOpen(false); }
    else props.onKeyDown?.(e);
  };

  return (
    <div ref={wrapRef} className={wrapClassName || "relative min-w-0 w-full flex-1"} data-testid="email-suggest-wrap">
      <input {...props} value={value} onChange={onChange} onKeyDown={onKeyDown} autoComplete="off" onFocus={(e) => { if (items.length) setOpen(true); props.onFocus?.(e); }} />
      {open && (
        <ul className={`absolute left-0 right-0 top-full mt-1 z-[70] max-h-56 overflow-y-auto border shadow-2xl ${light ? "bg-white border-slate-200" : "bg-[#0d1b30] border-white/15"}`} role="listbox" data-testid="email-suggest-list">
          {items.map((c, i) => (
            <li
              key={c.email}
              role="option"
              aria-selected={i === active}
              onMouseDown={(e) => { e.preventDefault(); pick(c.email); }}
              onMouseEnter={() => setActive(i)}
              className={`px-3 py-2 text-sm cursor-pointer flex items-center justify-between gap-3 ${light ? (i === active ? "bg-slate-100 text-slate-900" : "text-slate-700 hover:bg-slate-50") : (i === active ? "bg-white/10 text-white" : "text-slate-200 hover:bg-white/5")}`}
              data-testid={`email-suggest-item-${i}`}
            >
              <span className="truncate">{c.email}</span>
              {c.name ? <span className="text-[11px] text-slate-500 truncate">{c.name}</span> : c.count > 1 ? <span className="text-[10px] text-slate-500 tabular-nums">×{c.count}</span> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
